/* eslint-disable import/no-anonymous-default-export */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getAuthToken, removeAuthToken, removeUserData } from '../utils/storage';
import { API_ENDPOINTS } from '../constants';

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  // baseURL: process.env.REACT_APP_API_BASE_URL || 'https://api.sendmerchant.com/v1',
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000, // 15 seconds
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle unauthorized errors (401)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh the token
        const refreshToken = localStorage.getItem('send_merchant_refresh_token');
        if (refreshToken) {
          const response = await axios.post(API_ENDPOINTS.REFRESH_TOKEN, { refreshToken });
          const { token } = response.data;
          
          if (token) {
            // Update the token in storage
            localStorage.setItem('send_merchant_auth_token', token);
            
            // Retry the original request with the new token
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          }
        }
        
        // If refresh token is not available or refresh failed, logout
        removeAuthToken();
        removeUserData();
        window.location.href = '/login';
        
      } catch (refreshError) {
        // If token refresh fails, logout
        removeAuthToken();
        removeUserData();
        window.location.href = '/login';
      }
    }
    
    // Handle other errors
    return Promise.reject(error);
  }
);

/**
 * Generic API request function
 * @param method HTTP method
 * @param url API endpoint
 * @param data Request data
 * @param config Additional axios config
 * @returns Promise with response data
 */
const request = async <T>(
  method: string,
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await api({
      method,
      url,
      data: method !== 'GET' ? data : undefined,
      params: method === 'GET' ? data : undefined,
      ...config,
    });
    
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      // Extract error message from response
      const errorMessage = error.response.data?.message || error.message;
      throw new Error(errorMessage);
    }
    throw error;
  }
};

// Export API methods
export default {
  get: <T>(url: string, params?: any, config?: AxiosRequestConfig) => 
    request<T>('GET', url, params, config),
  
  post: <T>(url: string, data?: any, config?: AxiosRequestConfig) => 
    request<T>('POST', url, data, config),
  
  put: <T>(url: string, data?: any, config?: AxiosRequestConfig) => 
    request<T>('PUT', url, data, config),
  
  patch: <T>(url: string, data?: any, config?: AxiosRequestConfig) => 
    request<T>('PATCH', url, data, config),
  
  delete: <T>(url: string, config?: AxiosRequestConfig) => 
    request<T>('DELETE', url, undefined, config),
};
